// Small line diff (LCS after trimming common prefix/suffix) for previewing
// AI code changes. No dependency; fine for files of a few thousand lines.

export interface DiffLine {
  kind: ' ' | '-' | '+';
  text: string;
  oldNo?: number; // 1-based line in the old text
  newNo?: number; // 1-based line in the new text
}

/** Cells above which the LCS table is skipped (middle shown as replace). */
const MAX_CELLS = 4_000_000;

export function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split('\n');
  const b = newText.split('\n');
  let pre = 0;
  while (pre < a.length && pre < b.length && a[pre] === b[pre]) pre++;
  let suf = 0;
  while (suf < a.length - pre && suf < b.length - pre && a[a.length - 1 - suf] === b[b.length - 1 - suf]) suf++;

  const out: DiffLine[] = [];
  for (let i = 0; i < pre; i++) out.push({ kind: ' ', text: a[i], oldNo: i + 1, newNo: i + 1 });

  const am = a.slice(pre, a.length - suf);
  const bm = b.slice(pre, b.length - suf);
  const n = am.length, m = bm.length;
  if (n * m > MAX_CELLS) {
    am.forEach((t, i) => out.push({ kind: '-', text: t, oldNo: pre + i + 1 }));
    bm.forEach((t, j) => out.push({ kind: '+', text: t, newNo: pre + j + 1 }));
  } else {
    // lcs[i][j] = LCS length of am[i..] and bm[j..]
    const w = m + 1;
    const lcs = new Uint32Array((n + 1) * w);
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        lcs[i * w + j] = am[i] === bm[j] ? lcs[(i + 1) * w + j + 1] + 1 : Math.max(lcs[(i + 1) * w + j], lcs[i * w + j + 1]);
      }
    }
    let i = 0, j = 0;
    while (i < n || j < m) {
      if (i < n && j < m && am[i] === bm[j]) {
        out.push({ kind: ' ', text: am[i], oldNo: pre + i + 1, newNo: pre + j + 1 });
        i++; j++;
      } else if (i < n && (j >= m || lcs[(i + 1) * w + j] >= lcs[i * w + j + 1])) {
        // removals first on ties, like a conventional unified diff
        out.push({ kind: '-', text: am[i], oldNo: pre + i + 1 });
        i++;
      } else {
        out.push({ kind: '+', text: bm[j], newNo: pre + j + 1 });
        j++;
      }
    }
  }

  for (let k = 0; k < suf; k++) {
    const oi = a.length - suf + k, ni = b.length - suf + k;
    out.push({ kind: ' ', text: a[oi], oldNo: oi + 1, newNo: ni + 1 });
  }
  return out;
}

export function diffStats(lines: DiffLine[]): { added: number; removed: number } {
  let added = 0, removed = 0;
  for (const l of lines) {
    if (l.kind === '+') added++;
    else if (l.kind === '-') removed++;
  }
  return { added, removed };
}

/** Changed lines with `context` unchanged lines around them; gaps become null. */
export function diffHunks(lines: DiffLine[], context = 2): Array<DiffLine | null> {
  const keep = new Array(lines.length).fill(false);
  lines.forEach((l, i) => {
    if (l.kind === ' ') return;
    for (let k = Math.max(0, i - context); k <= Math.min(lines.length - 1, i + context); k++) keep[k] = true;
  });
  const out: Array<DiffLine | null> = [];
  let gap = false;
  lines.forEach((l, i) => {
    if (keep[i]) { out.push(l); gap = false; }
    else if (!gap && out.length > 0) { out.push(null); gap = true; }
  });
  if (out[out.length - 1] === null) out.pop();
  return out;
}
